// ============================================
// Profile - Selector Module (ES6)
// 프로필 선택기 (채팅용)
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading, escapeHtml } from '../utils.js';
import { loadProfiles, loadProfile, getProfileSelectorOptions, setProfileSelectorOptions } from './state.js';
import { openProfileFormModal } from './manager.js';
import { PROFILE_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { getCurrentUser, getCurrentRoomId, getCurrentSessionId, getCurrentChatData, getCurrentProfile, refreshChatUserMarkers } from '../app/state.js';
import { getCachedPrimaryProfileId, setCachedPrimaryProfileId } from '../auth/index.js';
import { ModalManager } from '../modal-manager.js';

// ============================================
// Close Profile Selector Modal
// ============================================

export function closeProfileSelector() {
  const modal = document.getElementById('profileSelectorModal');
  if (!modal) return;

  // 스택에서 먼저 제거 (히스토리 관리)
  ModalManager.remove('profileSelectorModal', true);

  // 닫힘 애니메이션 시작
  modal.classList.remove('open');

  // 애니메이션 완료 후 DOM에서 제거 (350ms = 300ms 애니메이션 + 여유)
  setTimeout(() => {
    if (modal.parentNode) {
      modal.remove();
    }
    ModalManager.unregister('profileSelectorModal');
  }, 350);
}

// ============================================
// Profile Selector Modal
// ============================================

export async function openProfileSelector(options = {}) {
  if (!getCurrentRoomId() || !getCurrentSessionId()) return;

  setProfileSelectorOptions(options);
  showGlobalLoading();
  try {
    const profiles = await loadProfiles();

    const selectedProfileId = getCurrentChatData()?.userProfile?.id ||
      getCurrentProfile()?.id || null;

    const primaryProfileId = getCachedPrimaryProfileId() || null;

    hideGlobalLoading();

    const modal = document.createElement('div');
    modal.className = 'bottom-sheet-overlay';
    modal.id = 'profileSelectorModal';
    modal.innerHTML = `
      <div class="bottom-sheet-backdrop" data-action="close"></div>
      <div class="bottom-sheet" style="max-height: 80vh;">
        <div class="bottom-sheet-handle"></div>
        <div class="bottom-sheet-header">
          <h3>프로필 선택</h3>
        </div>
        <div class="bottom-sheet-body">
          <div class="profile-selector-list" id="profileSelectorList">
            ${renderProfileSelectorItems(profiles, selectedProfileId, primaryProfileId)}
          </div>
          <button class="profile-add-btn" data-action="addProfile">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            새 프로필 추가
          </button>
        </div>
      </div>
    `;

    // 이벤트 위임 설정
    modal.addEventListener('click', (e) => {
      const actionEl = e.target.closest('[data-action]');
      if (!actionEl) return;

      const action = actionEl.dataset.action;
      const id = actionEl.dataset.id;

      if (action === 'close') {
        closeProfileSelector();
      } else if (action === 'addProfile') {
        createProfileFromSelector();
      } else if (action === 'selectProfile') {
        selectProfileFromSelector(id);
      } else if (action === 'setPrimary') {
        e.stopPropagation();
        setPrimaryProfileFromUI(id);
      } else if (action === 'editProfile') {
        e.stopPropagation();
        editProfileFromSelector(id);
      }
    });

    document.body.appendChild(modal);

    requestAnimationFrame(() => {
      modal.classList.add('open');
      ModalManager.register('profileSelectorModal', closeProfileSelector, 'bottomSheet');
      ModalManager.push('profileSelectorModal');
    });
  } catch (e) {
    hideGlobalLoading();
    showToast('프로필 로드 실패', 'error');
  }
}

function renderProfileSelectorItems(profiles, selectedProfileId, primaryProfileId) {
  if (profiles.length === 0) {
    return '<div class="profile-empty">등록된 프로필이 없습니다</div>';
  }

  return profiles.map(p => {
    const desc = p.description || '';
    const shortDesc = desc.length > 40 ? desc.substring(0, 40) + '...' : desc;
    const isPrimary = p.id === primaryProfileId;
    return `
    <div class="profile-selector-item ${selectedProfileId === p.id ? 'selected' : ''} ${isPrimary ? 'primary' : ''}" data-id="${p.id}">
      <div class="profile-selector-content" data-action="selectProfile" data-id="${p.id}">
        <div class="profile-selector-header">
          <span class="profile-selector-name">${escapeHtml(p.name)}</span>
          ${isPrimary ? '<span class="primary-badge">대표</span>' : ''}
          <span class="profile-selector-gender">${p.gender || '남성'}</span>
        </div>
        ${shortDesc ? `<div class="profile-selector-desc">${escapeHtml(shortDesc)}</div>` : ''}
      </div>
      <div class="profile-selector-actions">
        ${!isPrimary ? `
          <button class="set-primary-btn" data-action="setPrimary" data-id="${p.id}" title="대표 프로필로 설정">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </button>
        ` : ''}
        <button class="profile-edit-btn" data-action="editProfile" data-id="${p.id}" title="수정">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        ${selectedProfileId === p.id ? `
          <svg class="profile-check-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        ` : ''}
      </div>
    </div>
  `}).join('');
}

// ============================================
// Profile Selection
// ============================================

export async function selectProfileFromSelector(profileId) {
  const options = getProfileSelectorOptions();
  if (options.onSelect) {
    options.onSelect(profileId);
  } else {
    await selectProfile(profileId);
  }
}

export async function selectProfile(profileId) {
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();

  // 세션 구조 전용
  if (!roomId || !sessionId) return;

  showGlobalLoading();
  try {
    const response = await authFetch(PROFILE_ENDPOINTS.selectProfile, {
      body: { roomId, sessionId, profileId }
    });

    if (!response.ok) throw new Error('프로필 변경 실패');

    const result = await response.json();
    window.currentProfile = result.profile;
    const currentChatData = getCurrentChatData();
    if (currentChatData) {
      currentChatData.userProfile = result.profile;
    }

    updateProfileBadge();

    refreshChatUserMarkers();

    closeProfileSelector();
    showToast('프로필이 변경되었습니다');
  } catch (e) {
    showToast('프로필 변경 오류: ' + e.message, 'error');
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// Profile Selector Actions
// ============================================

export async function editProfileFromSelector(profileId) {
  const profiles = await loadProfiles();
  const profile = profiles.find(p => p.id === profileId);
  if (profile) {
    openProfileFormModal(profileId, profile, true);
  }
}

export function createProfileFromSelector() {
  openProfileFormModal(null, null, true);
}

export async function setPrimaryProfileFromUI(profileId) {
  const user = getCurrentUser();
  if (!user) return;

  showGlobalLoading();
  try {
    const response = await authFetch(PROFILE_ENDPOINTS.setPrimaryProfile, {
      body: { profileId: profileId }
    });

    if (!response.ok) throw new Error('대표 프로필 설정 실패');

    setCachedPrimaryProfileId(profileId);

    hideGlobalLoading();
    showToast('대표 프로필이 설정되었습니다');

    await refreshProfileSelectorList();

    if (document.getElementById('profileManagerModal')) {
      const { openProfileManager, closeProfileManager } = await import('./manager.js');
      closeProfileManager();
      openProfileManager();
    }
  } catch (e) {
    hideGlobalLoading();
    showToast('대표 프로필 설정 오류: ' + e.message, 'error');
  }
}

export async function refreshProfileSelectorList() {
  const listEl = document.getElementById('profileSelectorList');
  if (!listEl) return;

  const profiles = await loadProfiles(true);
  const selectedProfileId = getCurrentChatData()?.userProfile?.id ||
    getCurrentProfile()?.id || null;

  const primaryProfileId = getCachedPrimaryProfileId() || null;

  listEl.innerHTML = renderProfileSelectorItems(profiles, selectedProfileId, primaryProfileId);
}

// ============================================
// Profile Badge & Chat Profile
// ============================================

export function updateProfileBadge() {
  const badge = document.getElementById('currentProfileBadge');
  const nameSpan = document.getElementById('currentProfileName');
  const profileBar = document.getElementById('profileSelectBar');
  const inputArea = document.querySelector('.input-area');
  const sessionId = getCurrentSessionId();
  const currentProfile = getCurrentProfile();

  if (badge) {
    if (sessionId) {
      badge.style.display = 'flex';
      if (nameSpan) {
        nameSpan.textContent = currentProfile ? currentProfile.name : '프로필 선택';
      }
    } else {
      badge.style.display = 'none';
    }
  }

  if (sessionId) {
    if (!currentProfile && profileBar) {
      profileBar.style.display = 'block';
      if (inputArea) inputArea.style.display = 'none';
    } else if (profileBar) {
      profileBar.style.display = 'none';
      if (inputArea) inputArea.style.display = 'block';
    }
  } else {
    if (profileBar) profileBar.style.display = 'none';
    if (inputArea) inputArea.style.display = 'block';
  }
}

export async function loadChatProfile(chatData) {
  const currentChatData = getCurrentChatData();
  if (chatData?.userProfile && chatData.userProfile.id && chatData.userProfile.name && chatData.userProfile.name !== '없음') {
    window.currentProfile = chatData.userProfile;
    if (currentChatData) {
      currentChatData.userProfile = chatData.userProfile;
    }
  } else if (chatData?.selectedProfileId) {
    window.currentProfile = await loadProfile(chatData.selectedProfileId);
    if (currentChatData && window.currentProfile) {
      currentChatData.userProfile = window.currentProfile;
    }
  } else {
    window.currentProfile = null;
  }
  updateProfileBadge();
}
